import React, { FC, Fragment, useEffect, useState } from 'react';

import Dialog from '@mui/material/Dialog';
import { Button, DialogActions, DialogContent, DialogTitle } from '@mui/material';

import styles from './PortfolioDetailsDialog.module.scss';
import { PortfolioModel } from '../PortfolioModel';
import { PortfolioService } from '../PortfolioService';
import PortfolioEventsTable from './PortfolioEventsTable/PortfolioEventsTable';
import ConfirmationDialog from 'dialogs/ConfirmationDialog/ConfirmationDialog';
import PortfolioManagersTable from './PortfolioManagersTable/PortfolioManagersTable';
import PortfolioPowerplantsTable from './PortfolioPowerplantsTable/PortfolioPowerplantsTable';
import PortfolioEditableFields from './PortfolioEditableFields/PortfolioEditableFields';
import { AlertConfigModel } from 'dialogs/AlertDialog/AlertConfigModel';
import AlertDialog from 'dialogs/AlertDialog/AlertDialog';

interface PortfolioDetailsDialogProps {
  onClose: () => void;
  portfolioId: string;
  onPortfolioUpdate: () => void;
}

const PortfolioDetailsDialog: FC<PortfolioDetailsDialogProps> = (props: PortfolioDetailsDialogProps) => {

  const [currentAlert, setCurrentAlert] = useState<AlertConfigModel | null>(null);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState<boolean>(false);
  const [isDeletePortfolioConfirmationDialogOpen, setIsDeletePortfolioConfirmationDialogOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [portfolio, setPortfolio] = useState<PortfolioModel | null>();

  const isPersonal = portfolio?.type === 'personal';

  useEffect(() => {
    if (props.portfolioId) {
      getPortfolio(props.portfolioId);
    }
  }, [props.portfolioId]);

  const cancelPortfolioDeletionHandler = () => {
    setIsDeletePortfolioConfirmationDialogOpen(false);
  };

  const closeDialogHandler = (event?: Object, reason?: any) => {
    if (reason && reason === 'backdropClick') return;

    setPortfolio(null);
    props.onClose();
  };

  const confirmPortfolioDeletionHandler = async (): Promise<void> => {
    setIsDeletePortfolioConfirmationDialogOpen(false);
    try {
      const deletePortfolioResult = await PortfolioService.deletePortfolio(props.portfolioId);

      if (!deletePortfolioResult) {
        throw new Error('Error deleting portfolio');
      }

      props.onPortfolioUpdate();
      closeDialogHandler();
    } catch (error) {
      console.error('Failed at deleting portfolio:', error);
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed at deleting portfolio. Please try again, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    }
  };

  const getPortfolio = async (portfolioId: string): Promise<void> => {
    setIsLoading(true);

    try {
      const fetchedPortfolio = await PortfolioService.getPortfolio(portfolioId);

      if (!fetchedPortfolio) {
        throw new Error('Error getting portfolio');
      }

      setPortfolio(fetchedPortfolio);
    } catch (error) {
      console.error('Failed getting portfolio:', error);
      setPortfolio(null);
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed at getting portfolio. Please try again, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    } finally {
      setIsLoading(false);
    }

  };

  const openDeletePortfolioConfirmationDialogHandler = () => {
    setIsDeletePortfolioConfirmationDialogOpen(true);
  };

  const portfolioUpdateHandler = async (portfolioId: string): Promise<void> => {
    await getPortfolio(portfolioId);
    props.onPortfolioUpdate();
  };

  const portfolioDetailsGrid: JSX.Element = (
    <div className={styles.portfolioDetailsGrid}>
      <div className={styles.portfolioEditables}>
        { portfolio && (
            <PortfolioEditableFields
              portfolio={portfolio}
              onPortfolioUpdate={portfolioUpdateHandler}
            />
          )
        }

        { !portfolio && 'No portfolio data'}
      </div>

      <div className={styles.portfolioEvents}>
        { portfolio &&
          <PortfolioEventsTable portfolio={portfolio} isLoading={isLoading} />
        }
      </div>

      <div className={styles.portfolioManagers}>
        { portfolio &&
          <PortfolioManagersTable portfolio={portfolio} isLoading={isLoading} onPortfolioUpdate={portfolioUpdateHandler} />
        }
      </div>

      <div className={styles.portfolioPowerplants}>
        { portfolio &&
          <PortfolioPowerplantsTable portfolio={portfolio} isLoading={isLoading} onPortfolioUpdate={portfolioUpdateHandler} />
        }
      </div>
    </div>
  )

  return (
    <Fragment>
      <Dialog
        className={styles.PortfolioDetailsDialog}
        data-testid="PortfolioDetailsDialog"
        open={true}
        onClose={closeDialogHandler}
        fullWidth
        maxWidth={'xl'}
        scroll={'body'}
      >
        <DialogTitle>
          Portfolio Details

          <Button
            style={{ float: 'right' }}
            variant="contained"
            color={'error'}
            onClick={openDeletePortfolioConfirmationDialogHandler}
            disabled={isPersonal || isLoading}
          >
            Delete Portfolio
          </Button>

          <ConfirmationDialog
            open={isDeletePortfolioConfirmationDialogOpen}
            title="Delete Portfolio"
            message="Are you sure you want to delete this portfolio?"
            onConfirm={confirmPortfolioDeletionHandler}
            onCancel={cancelPortfolioDeletionHandler}
          />
        </DialogTitle>

        <DialogContent
          dividers={true}
          className={styles.portfolioDetailsContent}
        >
          {portfolioDetailsGrid}
        </DialogContent>

        <DialogActions className={styles.portfolioDetailsActions}>
          <Button
            variant="contained"
            onClick={closeDialogHandler}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {isAlertDialogOpen && currentAlert &&
        <AlertDialog
          onClose={() => {
            setIsAlertDialogOpen(false);
            setCurrentAlert(null);
          }}
          title={currentAlert.title}
          message={currentAlert.message}
        ></AlertDialog>
      }
    </Fragment>
  );
}

export default PortfolioDetailsDialog;
